import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'orange' | 'pink' | 'teal' | 'cyan' | 'indigo' | 'amber' | 'gray';
  isLoading?: boolean;
  onClick?: () => void;
}

const colorClasses: Record<string, { bg: string; border: string; icon: string; text: string; value: string }> = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'bg-blue-100 text-blue-600',
    text: 'text-blue-700',
    value: 'text-blue-900',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'bg-green-100 text-green-600',
    text: 'text-green-700',
    value: 'text-green-900',
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'bg-red-100 text-red-600',
    text: 'text-red-700',
    value: 'text-red-900',
  },
  yellow: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'bg-yellow-100 text-yellow-600',
    text: 'text-yellow-700',
    value: 'text-yellow-900',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: 'bg-purple-100 text-purple-600',
    text: 'text-purple-700',
    value: 'text-purple-900',
  },
  orange: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'bg-orange-100 text-orange-600',
    text: 'text-orange-700',
    value: 'text-orange-900',
  },
  pink: {
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    icon: 'bg-pink-100 text-pink-600',
    text: 'text-pink-700',
    value: 'text-pink-900',
  },
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    icon: 'bg-teal-100 text-teal-600',
    text: 'text-teal-700',
    value: 'text-teal-900',
  },
  cyan: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    icon: 'bg-cyan-100 text-cyan-600',
    text: 'text-cyan-700',
    value: 'text-cyan-900',
  },
  indigo: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    icon: 'bg-indigo-100 text-indigo-600',
    text: 'text-indigo-700',
    value: 'text-indigo-900',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'bg-amber-100 text-amber-600',
    text: 'text-amber-700',
    value: 'text-amber-900',
  },
  gray: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'bg-gray-100 text-gray-600',
    text: 'text-gray-700',
    value: 'text-gray-900',
  },
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  color,
  isLoading,
  onClick,
}) => {
  const colors = colorClasses[color] || colorClasses.gray;

  if (isLoading) {
    return (
      <div className={`${colors.bg} ${colors.border} border rounded-xl p-4 animate-pulse`}>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
          <div className={`p-2.5 rounded-lg ${colors.icon}`}>
            <div className="w-5 h-5"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${colors.bg} ${colors.border} border rounded-xl p-4 transition-all hover:shadow-md ${
        onClick ? 'cursor-pointer hover:scale-[1.02]' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs font-medium uppercase tracking-wide ${colors.text}`}>
            {title}
          </p>
          <p className={`text-2xl font-bold mt-1 ${colors.value}`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
          {trend && trendValue && (
            <div className="flex items-center mt-2 text-xs">
              {trend === 'up' && (
                <>
                  <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
                  <span className="text-green-600">{trendValue}</span>
                </>
              )}
              {trend === 'down' && (
                <>
                  <TrendingDown className="w-3 h-3 text-red-500 mr-1" />
                  <span className="text-red-600">{trendValue}</span>
                </>
              )}
              {trend === 'neutral' && (
                <>
                  <Minus className="w-3 h-3 text-gray-500 mr-1" />
                  <span className="text-gray-600">{trendValue}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className={`p-2.5 rounded-lg ${colors.icon}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default StatCard;
